import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { OverlayConfigContext, OverlayVisibilityContext } from '../context';
import { Modal } from './Modal/Modal';

/**
 * Renders the contact widget inside the reimplemented Modal, portalled onto the
 * consumer-provided mount point.
 *
 * eb-ui lazy-imported `react-dom` + `@eventbrite/eds-modal` once the overlay
 * became visible. In Valkyrie the Modal is a local reimplementation (documented
 * Marmalade gap) and `react-dom`'s `createPortal` is a stable import, so we
 * portal directly. The mount point resolution (domNode > domNodeId) is
 * preserved; if neither resolves we render nothing (faithful to eb-ui).
 */

interface ContactOrganizerRendererProps {
    domNodeId?: string;
    domNode?: Element;
    children?: React.ReactNode;
}

const getMountingPoint = (
    domNodeId?: string,
    domNode?: Element,
): Element | null => {
    if (domNode) {
        return domNode;
    }
    if (domNodeId && typeof document !== 'undefined') {
        return document.getElementById(domNodeId);
    }

    return null;
};

const RenderViaPortal: React.FunctionComponent<ContactOrganizerRendererProps> = (
    props,
) => {
    const { overlayConfig } = useContext(OverlayConfigContext);
    const { isVisible, setVisible } = useContext(OverlayVisibilityContext);
    const [mountPoint, setMountPoint] = useState<Element | null>(null);

    useEffect(() => {
        setMountPoint(getMountingPoint(props.domNodeId, props.domNode));
    }, [props.domNodeId, props.domNode]);

    if (!mountPoint) {
        return null;
    }

    return createPortal(
        <div className="contact-org-wrapper">
            <Modal
                title={overlayConfig?.title ?? ''}
                isShown={!!isVisible}
                onClose={() => setVisible && setVisible({ value: false })}
                onPrimaryAction={overlayConfig?.onPrimaryAction}
                primaryText={overlayConfig?.primaryText}
                primaryType={overlayConfig?.primaryType}
                onSecondaryAction={overlayConfig?.onSecondaryAction}
                secondaryText={overlayConfig?.secondaryText}
                secondaryType={overlayConfig?.secondaryType}
            >
                {props.children}
            </Modal>
        </div>,
        mountPoint,
    );
};

/**
 * Controls the rendering/hiding of the overlay that hosts the contact widget.
 */
export const ContactOrganizerRenderer: React.FunctionComponent<
    ContactOrganizerRendererProps
> = (props) => (
    <RenderViaPortal domNodeId={props.domNodeId} domNode={props.domNode}>
        {props.children}
    </RenderViaPortal>
);
